import classNames from 'classnames';
import React from 'react';
import { Button } from 'reactstrap';
import { createSelector } from 'reselect';

import { LocalizedMessage } from 'core/l10n/components';
import * as OvationsApi from 'core/ovations-api';
import QuestionFieldProps from 'core/ovations-components/definitions/QuestionFieldProps';
import FileUploader, { ImageFile, setPreview } from 'core/ovations-components/FileUploader';
import { questionFieldIsDisabled, questionFieldIsRequired } from 'core/ovations-components/QuestionField';

export type FileUploadQuestionProps = QuestionFieldProps<
    OvationsApi.Types.PromotionFileUploadQuestion,
    OvationsApi.Types.PromotionFileUploadQuestionAnswer,
    React.InputHTMLAttributes<HTMLInputElement>
>;

interface FileUploadQuestionState {
    isRejected: boolean;
}

class FileUploadQuestionField extends React.Component<FileUploadQuestionProps, FileUploadQuestionState> {
    canPreview = createSelector(
        (answer: OvationsApi.Types.PromotionFileUploadQuestionAnswer | undefined) => answer,
        (answer) => {
            if (!answer) {
                return false;
            }
            const { file, fileLocation } = answer;
            if (file) {
                return file.type.startsWith('image/');
            }
            if (!fileLocation) {
                return false;
            }
            const previewableFilePattern = /\.(jpe?g|png)$/i;
            return previewableFilePattern.test(fileLocation);
        },
    );

    constructor(props: FileUploadQuestionProps) {
        super(props);
        this.state = {
            isRejected: false,
        };
    }

    onDrop = (files: ImageFile[]) => {
        if (!this.props.onChange || !files.length) {
            return;
        }

        this.setState({
            isRejected: false,
        });
        const file = files[0];
        setPreview(file);
        this.props.onChange({
            type: OvationsApi.Enums.QuestionType.FileUpload,
            fileLocation: file.preview || null,
            fileName: null,
            file: files[0],
            fileUrl: null,
        });
    };

    onAnswerClick: React.MouseEventHandler<HTMLElement> = () => {
        if (this.props.onAnswerClick) {
            this.props.onAnswerClick(this.props.promotionQuestion);
        }
    };

    getFilePreview() {
        const { answer } = this.props;
        if (!answer || !this.canPreview(answer)) {
            return '';
        }
        return answer.fileUrl || answer.fileLocation || '';
    }

    getFileName() {
        const { answer } = this.props;
        if (answer) {
            return answer.file ? answer.file.name : answer.fileName;
        }
        return <LocalizedMessage id="questionForm_file_nameUnavailable" />;
    }

    renderWithoutFile() {
        const { inputProps = {} } = this.props;
        const acceptedFileTypes = this.props.promotionQuestion.allowedFileTypes || [];
        const maxFileSizeMb = this.props.promotionQuestion.question.maxFileSizeMb || 5;
        const hasFile = !!(this.props.answer && this.props.answer.fileLocation);
        const required = questionFieldIsRequired(this.props);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const acceptValue: any[] = [];
        acceptedFileTypes.map((type) => {
            if (type === '.png') {
                acceptValue['image/png'] = ['.png'];
            } else if (type === '.jpg') {
                acceptValue['image/jpg'] = ['.jpg'];
            } else if (type === '.jpeg') {
                acceptValue['image/jpeg'] = ['.jpeg'];
            } else if (type === '.doc' || type === '.docx') {
                acceptValue['application/msword'] = ['.doc', '.docx'];
            } else if (type === '.pdf') {
                acceptValue['application/pdf'] = ['.pdf'];
            }

            return;
        });

        return (
            <div>
                <div className="d-inline-block">
                    <FileUploader
                        disabled={questionFieldIsDisabled(this.props)}
                        required={questionFieldIsRequired(this.props)}
                        onDrop={this.onDrop}
                        onDropRejected={() => {
                            this.setState({ isRejected: true });
                        }}
                        onClick={this.onAnswerClick}
                        accept={Object.assign(acceptValue)}
                        inputProps={{
                            className: 'form-control',
                            name: this.props.promotionQuestion.id,
                            ...inputProps,
                        }}
                        className={classNames('drop-area mb-1', inputProps.className, {
                            'drop-area--selected': this.props.selected,
                            'drop-area--invalid': !hasFile && required,
                        })}
                        maxSize={maxFileSizeMb ? maxFileSizeMb * 1000000 : undefined}
                        activeClassName="drop-area mb-1 drop-area--accept"
                        acceptClassName="drop-area mb-1 drop-area--accept"
                        rejectClassName="drop-area mb-1 drop-area--accept"
                        disabledClassName="drop-area mb-1 drop-area--disabled"
                    >
                        <LocalizedMessage id="questionForm_file_uploadPrompt" />
                    </FileUploader>
                </div>
                <div>
                    <small className={classNames({ 'text-danger': this.state.isRejected })}>
                        <strong>
                            <LocalizedMessage id="questionForm_file_label_acceptedTypes" />:
                        </strong>{' '}
                        {acceptedFileTypes.join(', ')}
                        <br />
                        <strong>
                            <LocalizedMessage id="questionForm_file_label_maxSize" />:
                        </strong>{' '}
                        {maxFileSizeMb}MB
                    </small>
                    <div className={classNames('input-proximity-error', { 'is-visible': !hasFile && required })}>
                        <span className="invalid-feedback">
                            <LocalizedMessage id="questionForm_errorMessage_requiredField" />
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    renderFilePreview() {
        const filePreview = this.getFilePreview();
        if (!filePreview) {
            return (
                <span>
                    <LocalizedMessage id="questionForm_file_previewNotAvailable" />
                </span>
            );
        }

        return <img src={filePreview} alt="" />;
    }

    renderWithFile() {
        const classes = classNames('drop-area drop-area--hasImage', {
            'drop-area--clickable': !!this.props.onAnswerClick,
            'drop-area--selected': this.props.selected,
        });
        return (
            <>
                {!this.props.onAnswerClick ? (
                    <div className={classes}>{this.renderFilePreview()}</div>
                ) : (
                    <button type="button" className={classes} onClick={this.onAnswerClick}>
                        {this.renderFilePreview()}
                    </button>
                )}
                <small>
                    <strong>
                        <LocalizedMessage id="questionForm_file_label_name" />
                    </strong>
                    : {this.getFileName()}
                    <br />
                    {!questionFieldIsDisabled(this.props) && (
                        <Button
                            onClick={() => {
                                if (!this.props.onChange) {
                                    return;
                                }
                                window.URL.revokeObjectURL(this.getFilePreview());
                                this.props.onChange({
                                    type: OvationsApi.Enums.QuestionType.FileUpload,
                                    fileLocation: null,
                                    fileName: null,
                                    file: null,
                                    fileUrl: null,
                                });
                            }}
                            className="p-0"
                            color="link"
                            size="sm"
                        >
                            <LocalizedMessage id="questionForm_file_button_removeFile" />
                        </Button>
                    )}
                </small>
            </>
        );
    }

    render() {
        const hasFile = this.props.answer && this.props.answer.fileLocation;
        if (hasFile) {
            return this.renderWithFile();
        }
        return this.renderWithoutFile();
    }
}

export default FileUploadQuestionField;
